import {TEntity} from 'modules/data/data-types';

import {
    BASE_COLUMNS,
    BASE_COLUMNS_WIDTH,
    FIELD_PATTERN,
    FIELD_PREFIX,
    UNIFIED_FIELD_LABEL_PREFIX,
    UNIFIED_FIELD_WIDTH,
} from './open-interface-constants';
import {IColumnConfig} from './open-interface-types';

/**
 * Извлекает номера полей из данных на основе паттерна field_XXX
 * @param data - Массив объектов с данными
 * @returns Множество уникальных номеров полей
 */
const extractFieldNumbers = (data: Record<string, any>[]): Set<number> => {
    const fieldNumbers = new Set<number>();

    data.forEach(item => {
        Object.keys(item).forEach(key => {
            const match = key.match(FIELD_PATTERN);
            if (match) {
                fieldNumbers.add(parseInt(match[1], 10));
            }
        });
    });

    return fieldNumbers;
};

/**
 * Формирует ключ поля в формате field_XXX с ведущими нулями
 * @param fieldNumber - Номер поля
 * @returns Строковый ключ поля
 */
const getFieldKey = (fieldNumber: number): string => `${FIELD_PREFIX}${fieldNumber.toString().padStart(3, '0')}`;

/**
 * Проверяет, имеет ли значение валидное содержимое
 * @param value - Проверяемое значение
 * @returns true если значение не null, undefined или пустая строка
 */
const hasValidValue = (value: any): boolean => value !== null && value !== undefined && value !== '';

/**
 * Фильтрует номера полей, оставляя только те, которые содержат данные
 * @param fieldNumbers - Массив номеров полей для проверки
 * @param data - Массив объектов с данными
 * @returns Массив номеров полей, содержащих валидные данные
 */
const filterFieldsWithData = (
    fieldNumbers: number[],
    data: Record<string, any>[],
): number[] => fieldNumbers.filter(fieldNumber => {
    const fieldKey = getFieldKey(fieldNumber);
    return data.some(item => hasValidValue(item[fieldKey]));
});

/**
 * Создает конфигурацию колонки для унифицированного поля
 * @param fieldNumber - Номер поля
 * @param index - Индекс колонки
 * @returns Конфигурация колонки для унифицированного поля
 */
const createUnifiedFieldColumn = (
    fieldNumber: number,
    index: number,
): IColumnConfig => ({
    id: 3 + index,
    columnOrdinal: 3 + index,
    label: `${UNIFIED_FIELD_LABEL_PREFIX}${fieldNumber}`,
    type: 'text',
    fieldKey: getFieldKey(fieldNumber),
});

/**
 * Преобразует конфигурацию колонки в формат Entity для таблицы
 * @param columnConfig - Конфигурация колонки
 * @returns Entity объект для отображения в таблице
 */
const mapToTableColumn = (columnConfig: IColumnConfig): TEntity => ({
    title: columnConfig.label,
    dataIndex: columnConfig.fieldKey,
    key: columnConfig.id,
    width: columnConfig.fieldKey.includes(FIELD_PREFIX)
        ? UNIFIED_FIELD_WIDTH
        : BASE_COLUMNS_WIDTH,
});

/**
 * Генерирует колонки для унифицированных полей на основе данных
 * Создает базовые колонки и добавляет динамические колонки для полей с данными
 * @param data - Массив данных для анализа
 * @returns Массив Entity объектов для отображения в таблице
 */
export const generateUnifiedFieldsColumns = (data: TEntity[]): TEntity[] => {
    if (!data || data.length === 0) {
        return [];
    }

    const fieldNumbers = extractFieldNumbers(data);
    const sortedFieldNumbers = Array.from(fieldNumbers).sort((a, b) => a - b);

    if (sortedFieldNumbers.length === 0) {
        return [];
    }

    const fieldsWithData = filterFieldsWithData(sortedFieldNumbers, data);
    const rawColumns = [...BASE_COLUMNS];

    fieldsWithData.forEach((fieldNumber, index) => {
        rawColumns.push(createUnifiedFieldColumn(fieldNumber, index));
    });

    return rawColumns.map(mapToTableColumn);
};
